import type { ThreeEvent } from "@react-three/fiber";
import { useMemo } from "react";
import { Quaternion, Vector3 } from "three";
import { DRAG_COLORS } from "./dragTheme";

/** Arrow size constants, in millimetres. */
const ARROW_LENGTH = 60;
const ARROW_HEAD_LENGTH = 20;
const ARROW_HEAD_WIDTH = 16;
const ARROW_SHAFT_RADIUS = 2.5;
const ARROW_OUTSET = 15;

/** The arrow is modelled along +Y and rotated onto its direction. */
const ARROW_AXIS = new Vector3(0, 1, 0);

const HELPER_USER_DATA = { userData: { isDragHelper: true } };

/**
 * A 3D arrow pointing in a given direction, marking one way a part can move.
 *
 * Built from a cylinder and a cone rather than from `THREE.ArrowHelper`, whose
 * shaft is a `THREE.Line` and therefore always one device pixel wide no matter
 * what is asked of it — next to a solid cone head that reads as a floating
 * triangle on a thread. A meshed shaft also picks up the same perspective
 * foreshortening as the case, so the arrows sit on the part instead of hovering
 * in screen space.
 *
 * The arrow is occluded by the model, so it only shows where the part it
 * belongs to is actually visible.
 *
 * It is also a grab handle: pressing it starts a drag locked to its own axis.
 * The handlers belong to the whole arrow group, so the invisible grab tube
 * below — which is what the pointer actually hits — reports as the arrow.
 */
export const Arrow3D = ({
  direction,
  outset = ARROW_OUTSET,
  length = ARROW_LENGTH,
  headLength = ARROW_HEAD_LENGTH,
  headWidth = ARROW_HEAD_WIDTH,
  grabReachBack = 0,
  active = false,
  onPointerDown,
  onPointerEnter,
  onPointerMove,
  onPointerLeave,
}: {
  direction: Vector3;
  outset?: number;
  length?: number;
  headLength?: number;
  headWidth?: number;
  /**
   * How far back towards the part the grab area reaches, in millimetres.
   *
   * Exactly the gap the caller left between the part's surface and the arrow's
   * tail, so the two hit areas meet and the pointer can travel from one to the
   * other without passing over nothing. It must not be more than that: a grab
   * area reaching *into* the part would take presses meant for the part body
   * and turn a free drag into an axis-locked one.
   */
  grabReachBack?: number;
  /** Pointer is on this arrow, or it is driving the running drag. */
  active?: boolean;
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerEnter?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerMove?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerLeave?: (event: ThreeEvent<PointerEvent>) => void;
}) => {
  const { position, quaternion } = useMemo(() => {
    const dir = direction.clone().normalize();
    return {
      position: dir.clone().multiplyScalar(outset),
      // Handles the straight-down case on its own: `setFromUnitVectors` falls
      // back to an explicit orthogonal axis when the two vectors are opposed,
      // which is the 180° degeneracy Pitfall 8 used to work around by hand.
      quaternion: new Quaternion().setFromUnitVectors(ARROW_AXIS, dir),
    };
  }, [direction, outset]);

  const shaftLength = Math.max(length - headLength, 0);

  // Depth-tested, unlike the movement outline and the guide lines: the arrows
  // sit at the part itself and read as belonging to it, so drawing them over
  // whatever happens to be in front would put them in mid-air on the wrong
  // face. A part hidden behind the case is found by its glow, which does
  // shine through; the arrows appear once it is actually in view.
  //
  // Opaque and unlined. The arrows used to carry a dark inverted hull so an
  // amber arrow stayed legible on an amber-ish laminate; drawn in the part's
  // own blue they no longer need it, and the hull broke up along the shaft
  // wherever the grown back faces and the body fought over the same depth.
  //
  // Under the pointer it lightens to the glow's pale rim rather than changing
  // hue: the arrow is still the same affordance in the same colour family, and
  // a second accent would claim a state (snapped) that has not happened.
  const material = (
    // `toneMapped` off so the accent keeps the exact colour the theme names -
    // the scene's tone mapping is calibrated for materials, and would pull the
    // blue towards the rest of the render.
    <meshBasicMaterial
      color={active ? DRAG_COLORS.partRim : DRAG_COLORS.arrow}
      depthTest
      toneMapped={false}
    />
  );

  // The hit area, and the reason an arrow can be grabbed at all: the shaft is
  // 2.5 mm of geometry, a few pixels at any sane zoom, and the head is only
  // reliably hittable from the side. This tube is as wide as the head, runs
  // past the tip, and reaches `grabReachBack` towards the part so it meets the
  // part's own hit area — without that bridge the pointer crosses empty space
  // on the way to the arrow, the hover ends, and the arrows unmount before they
  // can be pressed.
  //
  // What it must never do is reach *into* the part. A tube overlapping the body
  // is nearer to the camera than the part's own surface wherever the arrow
  // points towards the viewer, so it silently took presses aimed at the part
  // and started an axis-locked drag from them — a part that could suddenly only
  // be moved one way, and arrows lighting up while the body was grabbed.
  //
  // `visible={false}` rather than a transparent material: three's raycaster
  // never looks at `visible` (see `partRaycast.ts`), so the mesh still takes
  // part in hit testing while being excluded from rendering entirely — no
  // transparent-sort entry and no depth write to disturb the render-order
  // bands.
  const grabTubeLength = length + grabReachBack;

  return (
    <group
      position={position}
      quaternion={quaternion}
      renderOrder={1000}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      {...HELPER_USER_DATA}
    >
      <mesh
        visible={false}
        position={[0, (length - grabReachBack) / 2, 0]}
        {...HELPER_USER_DATA}
      >
        <cylinderGeometry
          args={[headWidth / 2, headWidth / 2, grabTubeLength, 8]}
        />
      </mesh>
      <mesh
        position={[0, shaftLength / 2, 0]}
        renderOrder={1000}
        {...HELPER_USER_DATA}
      >
        <cylinderGeometry
          args={[ARROW_SHAFT_RADIUS, ARROW_SHAFT_RADIUS, shaftLength, 12]}
        />
        {material}
      </mesh>
      <mesh
        position={[0, length - headLength / 2, 0]}
        renderOrder={1000}
        {...HELPER_USER_DATA}
      >
        <coneGeometry args={[headWidth / 2, headLength, 16]} />
        {material}
      </mesh>
    </group>
  );
};
